import { toast } from 'react-toastify';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useCallback, useEffect, useState } from 'react';
import useApi from '../api';
import Confetti from 'react-confetti';
import { Loader2, SaveIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { getApiErrorMessage } from './saveErrors';

export interface AgentChange {
    path: string;
    label: string;
    section: string;
    kind: 'added' | 'removed' | 'modified';
    live: any;
    draft: any;
}

interface AgentChangesResponse {
    changes: AgentChange[];
    count: number;
    draft_updated_at: string | null;
    live_updated_at: string | null;
}

interface PublishAgentProps {
    agId: string;
    onSaveBeforePublish?: () => Promise<void>;
    onSaveDraft?: () => Promise<void> | void;
    isSavingDraft?: boolean;
    /** Draft version from the editor; changes are refetched whenever it moves. */
    draftUpdatedAt?: string | null;
    /** Bumped when another session publishes this agent, to refresh the badge. */
    publishRefreshKey?: number;
}

const SECTION_ORDER = [
    'Prompting',
    'Configuration',
    'Appearance',
    'Data Capture',
    'Workflow',
    'Skills',
    'Actions',
    'Voice',
    'Knowledge',
    'App Mode',
];

const humanizeKey = (key: string): string =>
    key
        .split(/(?=[A-Z])|_/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

const isEmptyValue = (value: any): boolean =>
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

/** One-line summary for an array item on the review screen. */
const itemSummary = (item: any): string => {
    if (item === null || item === undefined) return '(empty)';
    if (typeof item !== 'object') return String(item);
    const candidate = item.label ?? item.name ?? item.title ?? item.url ?? item.value;
    if (candidate !== undefined && candidate !== null && candidate !== '') return String(candidate);
    return Object.entries(item)
        .slice(0, 3)
        .map(([key, value]) => `${humanizeKey(key)}: ${typeof value === 'object' ? '…' : String(value)}`)
        .join(', ');
};

const relativeTime = (iso: string | null | undefined): string | null => {
    if (!iso) return null;
    const then = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`).getTime();
    if (Number.isNaN(then)) return null;
    const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
    const months = Math.floor(days / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
};

const KIND_STYLES: Record<AgentChange['kind'], { label: string; dotClass: string; textClass: string }> = {
    added: {
        label: 'Added',
        dotClass: 'bg-emerald-500',
        textClass: 'text-emerald-600 dark:text-emerald-400',
    },
    removed: {
        label: 'Removed',
        dotClass: 'bg-red-500',
        textClass: 'text-red-600 dark:text-red-400',
    },
    modified: {
        label: 'Changed',
        dotClass: 'bg-amber-500',
        textClass: 'text-amber-600 dark:text-amber-400',
    },
};

const ChangeKind = ({ kind }: { kind: AgentChange['kind'] }) => (
    <span className={`flex shrink-0 items-center gap-1.5 text-xs font-medium ${KIND_STYLES[kind].textClass}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${KIND_STYLES[kind].dotClass}`} />
        {KIND_STYLES[kind].label}
    </span>
);

const isImageChange = (change: AgentChange) => change.path === 'logo' || change.path === 'message_icon';

const ValueContent = ({ value, change }: { value: any; change: AgentChange }) => {
    if (isImageChange(change) && typeof value === 'string' && value) {
        return <img src={value} alt={change.label} className="h-10 w-10 rounded-md border object-cover" />;
    }
    if (isEmptyValue(value)) {
        return <span className="text-muted-foreground">—</span>;
    }
    if (typeof value === 'boolean') {
        return (
            <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${value ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
                {value ? 'On' : 'Off'}
            </span>
        );
    }
    if (Array.isArray(value)) {
        return (
            <div className="flex min-w-0 flex-col gap-1">
                {value.slice(0, 8).map((item, index) => (
                    <span key={index} className="max-w-full break-words rounded bg-muted px-1.5 py-0.5 text-xs [overflow-wrap:anywhere]" title={itemSummary(item)}>
                        {itemSummary(item)}
                    </span>
                ))}
                {value.length > 8 && (
                    <span className="text-xs text-muted-foreground">+{value.length - 8} more</span>
                )}
            </div>
        );
    }
    if (typeof value === 'object') {
        return (
            <div className="flex min-w-0 flex-col gap-0.5">
                {Object.entries(value).slice(0, 8).map(([key, entry]) => (
                    <span key={key} className="max-w-full break-words text-xs [overflow-wrap:anywhere]">
                        <span className="text-muted-foreground">{humanizeKey(key)}:</span>{' '}
                        {typeof entry === 'object' ? itemSummary(entry) : String(entry)}
                    </span>
                ))}
            </div>
        );
    }
    return <span className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{String(value)}</span>;
};

const ChangeRow = ({
    change,
    selected,
    onToggle,
}: {
    change: AgentChange;
    selected: boolean;
    onToggle: (checked: boolean) => void;
}) => (
    <div className={`min-w-0 max-w-full overflow-hidden rounded-xl border bg-background transition-opacity ${selected ? '' : 'opacity-50'}`}>
        <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
                <Checkbox
                    checked={selected}
                    onCheckedChange={(value) => onToggle(value === true)}
                    aria-label={`Include ${change.label}`}
                />
                <span className="min-w-0 break-words text-sm font-medium [overflow-wrap:anywhere]">
                    {change.label}
                </span>
            </div>
            <ChangeKind kind={change.kind} />
        </div>

        <div className="grid border-t bg-muted/[0.08] sm:grid-cols-2 sm:divide-x">
            <div className="min-w-0 p-4">
                <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Current
                </p>
                <div className="text-[13px] leading-5 text-muted-foreground">
                    <ValueContent value={change.live} change={change} />
                </div>
            </div>
            <div className="min-w-0 border-t p-4 sm:border-t-0">
                <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-foreground/60">
                    Draft
                </p>
                <div className="text-[13px] font-medium leading-5 text-foreground/90">
                    <ValueContent value={change.draft} change={change} />
                </div>
            </div>
        </div>
    </div>
);

const PublishAgent: React.FC<PublishAgentProps> = ({
    agId,
    onSaveBeforePublish,
    onSaveDraft,
    isSavingDraft = false,
    draftUpdatedAt = null,
    publishRefreshKey = 0,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [confettiVisible, setConfettiVisible] = useState(false);
    const [changes, setChanges] = useState<AgentChange[]>([]);
    // Paths the user unchecked in the review modal; everything is selected by
    // default, and the set resets whenever the change list is refetched.
    const [deselected, setDeselected] = useState<Set<string>>(new Set());
    const [reviewedDraftVersion, setReviewedDraftVersion] = useState<string | null>(null);
    const [lastPublishedAt, setLastPublishedAt] = useState<string | null>(null);
    const [preparingReview, setPreparingReview] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const { get, post } = useApi();

    const fetchChanges = useCallback(async () => {
        const response = await get(`/agents/${agId}/changes`);
        const data: AgentChangesResponse = response.data;
        setChanges(Array.isArray(data.changes) ? data.changes : []);
        setDeselected(new Set());
        setReviewedDraftVersion(data.draft_updated_at ?? null);
        setLastPublishedAt(data.live_updated_at ?? null);
        return data;
    }, [agId, get]);

    const toggleChange = (path: string, checked: boolean) => {
        setDeselected((previous) => {
            const next = new Set(previous);
            if (checked) next.delete(path);
            else next.add(path);
            return next;
        });
    };

    const toggleSection = (items: AgentChange[], checked: boolean) => {
        setDeselected((previous) => {
            const next = new Set(previous);
            items.forEach((change) => {
                if (checked) next.delete(change.path);
                else next.add(change.path);
            });
            return next;
        });
    };

    // The saved-draft version and cross-session publish events drive refreshes,
    // so the badge stays accurate without polling.
    useEffect(() => {
        if (!agId) return;
        fetchChanges().catch((error) => {
            console.error('Error fetching draft changes:', error);
        });
    }, [agId, draftUpdatedAt, publishRefreshKey, fetchChanges]);

    const openModal = async () => {
        setIsOpen(true);
        setPreparingReview(true);
        try {
            if (onSaveBeforePublish) {
                await onSaveBeforePublish();
            }
            await fetchChanges();
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Could not prepare changes for review'));
            setIsOpen(false);
        } finally {
            setPreparingReview(false);
        }
    };

    const numberOfChanges = changes.length;
    const selectedChanges = changes.filter((change) => !deselected.has(change.path));
    const selectedCount = selectedChanges.length;
    const isPartial = selectedCount < numberOfChanges;
    const lastPublishedLabel = relativeTime(lastPublishedAt);

    const publishAgent = async () => {
        setPublishing(true);
        try {
            const body: Record<string, any> = {
                expected_draft_updated_at: reviewedDraftVersion,
            };
            if (isPartial) {
                body.selected_paths = selectedChanges.map((change) => change.path);
            }
            const response = await post(`/agents/${agId}/publish`, body);
            toast.success(
                isPartial
                    ? `Published ${selectedCount} of ${numberOfChanges} changes`
                    : 'Agent published successfully'
            );
            setLastPublishedAt(response.data?.published_at ?? new Date().toISOString());
            setConfettiVisible(true);
            setTimeout(() => setConfettiVisible(false), 5000);
            setIsOpen(false);
            if (isPartial) {
                // Deselected changes are still unpublished; refresh the badge.
                await fetchChanges().catch(() => undefined);
            } else {
                setChanges([]);
                setDeselected(new Set());
            }
        } catch (error: any) {
            if (error?.detail?.code === 'agent_publish_conflict') {
                toast.warning('This draft changed since you opened the review. Showing the latest changes.');
                await fetchChanges().catch(() => undefined);
            } else {
                toast.error(getApiErrorMessage(error, 'Error publishing agent'));
            }
        } finally {
            setPublishing(false);
        }
    };

    const sections = SECTION_ORDER
        .concat(changes.map((change) => change.section).filter((section) => !SECTION_ORDER.includes(section)))
        .filter((section, index, arr) => arr.indexOf(section) === index)
        .map((section) => ({
            section,
            items: changes.filter((change) => change.section === section),
        }))
        .filter(({ items }) => items.length > 0);

    return (
        <>
            {confettiVisible && <Confetti recycle={false} />}
            {!isOpen && (
                <div className="flex items-center gap-2">
                    {numberOfChanges > 0 && (
                        <div className="mr-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                            {numberOfChanges} unpublished change{numberOfChanges > 1 ? 's' : ''}
                        </div>
                    )}
                    <Button
                        onClick={() => { if (onSaveDraft) onSaveDraft(); }}
                        className="h-9 gap-1.5 px-3 font-medium"
                        variant="outline"
                        size="sm"
                        disabled={isSavingDraft || publishing}
                    >
                        {isSavingDraft ? (
                            <>
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <SaveIcon className="h-3.5 w-3.5 text-muted-foreground" />
                                Save draft
                            </>
                        )}
                    </Button>
                    <Button
                        onClick={openModal}
                        disabled={isSavingDraft || publishing}
                        size="sm"
                        className="h-9 px-4 font-medium"
                    >
                        Publish
                    </Button>
                </div>
            )}

            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0">
                    <DialogHeader className="border-b px-6 py-5 pr-12 text-left">
                        <DialogTitle className="text-lg font-semibold tracking-tight">
                            Review changes
                        </DialogTitle>
                        <DialogDescription className="mt-1">
                            {preparingReview
                                ? 'Preparing the latest draft for review...'
                                : numberOfChanges > 0
                                    ? isPartial
                                        ? `${selectedCount} of ${numberOfChanges} changes will go live. Unchecked changes stay in your draft.`
                                        : `${numberOfChanges} unpublished change${numberOfChanges > 1 ? 's' : ''} will go live.`
                                    : 'Your live agent already matches this draft.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="max-h-[60vh] w-full overflow-y-auto overflow-x-hidden bg-muted/[0.16] px-6 py-5">
                        {preparingReview ? (
                            <div className="flex h-48 items-center justify-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Saving draft and collecting changes...
                            </div>
                        ) : numberOfChanges > 0 ? (
                            <div className="flex flex-col gap-6">
                                {sections.map(({ section, items }) => {
                                    const selectedInSection = items.filter((change) => !deselected.has(change.path)).length;
                                    return (
                                        <section key={section}>
                                            <div className="mb-2.5 flex items-center justify-between">
                                                <div className="flex items-center gap-2.5">
                                                    <Checkbox
                                                        checked={
                                                            selectedInSection === items.length
                                                                ? true
                                                                : selectedInSection === 0
                                                                    ? false
                                                                    : 'indeterminate'
                                                        }
                                                        onCheckedChange={(value) => toggleSection(items, value === true)}
                                                        aria-label={`Include all ${section} changes`}
                                                        className="data-[state=indeterminate]:bg-primary/40 data-[state=indeterminate]:text-primary-foreground"
                                                    />
                                                    <h3 className="text-xs font-semibold text-foreground/80">
                                                        {section}
                                                    </h3>
                                                </div>
                                                <span className="text-xs tabular-nums text-muted-foreground">
                                                    {selectedInSection === items.length
                                                        ? items.length
                                                        : `${selectedInSection}/${items.length}`}
                                                </span>
                                            </div>
                                            <div className="flex flex-col gap-3">
                                                {items.map((change) => (
                                                    <ChangeRow
                                                        key={change.path}
                                                        change={change}
                                                        selected={!deselected.has(change.path)}
                                                        onToggle={(checked) => toggleChange(change.path, checked)}
                                                    />
                                                ))}
                                            </div>
                                        </section>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="flex h-48 flex-col items-center justify-center gap-1.5 text-sm">
                                <p className="font-medium text-foreground">Everything is up to date</p>
                                <p className="text-xs text-muted-foreground">
                                    There are no changes to publish.
                                </p>
                            </div>
                        )}
                    </div>

                    {isPartial && selectedCount > 0 && (
                        <div className="border-t bg-amber-500/10 px-6 py-2.5 text-xs text-amber-700 dark:text-amber-400">
                            You&apos;re publishing a combination of draft and live settings that you
                            haven&apos;t previewed together. Unchecked changes stay in your draft.
                        </div>
                    )}

                    <div className="relative z-10 flex items-center justify-between gap-4 border-t bg-background px-6 py-4">
                        <div className="text-xs text-muted-foreground">
                            {lastPublishedLabel && (
                                <span>Last published {lastPublishedLabel}</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsOpen(false)}
                                disabled={publishing}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={publishAgent}
                                disabled={publishing || preparingReview || selectedCount === 0}
                                className="min-w-36"
                            >
                                {publishing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {publishing
                                    ? 'Publishing...'
                                    : numberOfChanges === 0
                                        ? 'Publish agent'
                                        : isPartial
                                            ? `Publish ${selectedCount} of ${numberOfChanges}`
                                            : `Publish ${numberOfChanges} change${numberOfChanges > 1 ? 's' : ''}`}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default PublishAgent;
